'use client';
import React, { useEffect, useRef } from 'react';

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef?.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(30px)';
    const timer = setTimeout(() => {
      el.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="pt-28 pb-0 bg-white relative overflow-hidden">
      {/* Warm tinted hero bg */}
      <div className="absolute inset-0 bg-[#F8F4F1] rounded-b-[40px] mx-4 top-0 bottom-0" />
      <div className="max-w-[1200px] mx-auto px-6 relative z-10">
        <div ref={heroRef} className="text-center">
          {/* Social proof pill */}
          <div className="inline-flex items-center gap-3 bg-white rounded-full px-4 py-2 shadow-sm mb-8">
            <div className="flex -space-x-2">
              {[1,2,3]?.map(i => (
                <div key={i} className="w-7 h-7 rounded-full border-2 border-white overflow-hidden bg-theme-01">
                  <img
                    src={`https://i.pravatar.cc/40?img=${i + 10}`}
                    alt={`User ${i}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            <span className="text-[14px] font-medium text-dark">12,000+ Successful Matches Made</span>
          </div>

          {/* Main heading */}
          <h1 className="font-bold text-dark leading-[1.1] mb-6 text-[56px] md:text-[72px] lg:text-[80px] tracking-tight max-w-[900px] mx-auto">
            The marketplace for
            <br />
            <span className="relative inline-block">
              TikTok Live
              <span className="absolute -bottom-1 left-0 right-0 h-3 bg-theme-02 -z-10 rounded-sm" />
            </span>
            {' '}Stream Talent
          </h1>

          {/* Subheading */}
          <div className="max-w-[620px] mx-auto mb-10">
            <p className="text-[18px] md:text-[20px] text-dark opacity-70 leading-relaxed">
              Connect top-performing TikTok Shop live sellers with brands ready to scale. Find your perfect match — fast.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <a
              href="#"
              className="font-bold text-[16px] text-dark bg-theme-04 px-8 py-4 rounded-pill hover:brightness-95 transition-all duration-200 shadow-sm"
            >
              I&apos;m a Seller
            </a>
            <a
              href="#"
              className="font-bold text-[16px] text-dark bg-white border border-dark/20 px-8 py-4 rounded-pill hover:bg-neutral-01 transition-all duration-200"
            >
              I&apos;m a Brand
            </a>
          </div>

          {/* Floating labels */}
          <div className="relative">
            <div className="absolute left-4 md:left-16 top-8 z-20">
              <div className="flex items-center gap-2">
                <div className="bg-theme-02 text-dark text-[13px] font-semibold px-4 py-2 rounded-full shadow-sm">
                  Seller
                </div>
                <svg width="40" height="20" viewBox="0 0 60 20" fill="none" className="hidden md:block">
                  <path d="M0 10 Q30 0 60 10" stroke="#120A0B" strokeWidth="1.5" fill="none" strokeDasharray="4 2"/>
                  <polygon points="55,6 60,10 55,14" fill="#120A0B"/>
                </svg>
              </div>
            </div>
            <div className="absolute right-4 md:right-16 top-8 z-20">
              <div className="flex items-center gap-2">
                <svg width="40" height="20" viewBox="0 0 60 20" fill="none" className="hidden md:block">
                  <path d="M60 10 Q30 0 0 10" stroke="#120A0B" strokeWidth="1.5" fill="none" strokeDasharray="4 2"/>
                  <polygon points="5,6 0,10 5,14" fill="#120A0B"/>
                </svg>
                <div className="bg-theme-01 text-white text-[13px] font-semibold px-4 py-2 rounded-full shadow-sm">
                  Brand
                </div>
              </div>
            </div>

            {/* Dashboard mockup */}
            <div className="relative mx-auto max-w-[900px] rounded-t-[24px] overflow-hidden shadow-2xl border border-dark/10">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DashboardMockup() {
  return (
    <div className="bg-white p-4 md:p-6">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-neutral-02">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-dark flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L18 7V13L10 18L2 13V7L10 2Z" fill="#C9FF85" />
            </svg>
          </div>
          <span className="font-bold text-dark text-sm">Talent Dashboard</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-theme-green" />
          <span className="text-xs text-dark/60 font-medium">Live Now: 847 streams</span>
        </div>
      </div>
      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Active Sellers', value: '12,400+', color: 'bg-theme-01', textColor: 'text-white' },
          { label: 'Brands Hiring', value: '3,200+', color: 'bg-theme-04', textColor: 'text-dark' },
          { label: 'Matches Made', value: '48,900', color: 'bg-theme-02', textColor: 'text-dark' },
          { label: 'Avg. GMV/Stream', value: '$24,500', color: 'bg-theme-03', textColor: 'text-dark' },
        ]?.map((stat) => (
          <div key={stat?.label} className={`${stat?.color} rounded-2xl p-3 md:p-4`}>
            <p className={`text-[11px] font-medium ${stat?.textColor} opacity-70 mb-1`}>{stat?.label}</p>
            <p className={`text-lg md:text-xl font-bold ${stat?.textColor}`}>{stat?.value}</p>
          </div>
        ))}
      </div>
      {/* Seller cards row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { name: 'Sarah K.', niche: 'Beauty & Skincare', gmv: '$180K/mo', rating: 4.9, img: 15 },
          { name: 'Marcus T.', niche: 'Fashion & Apparel', gmv: '$240K/mo', rating: 5.0, img: 20 },
          { name: 'Priya M.', niche: 'Health & Wellness', gmv: '$95K/mo', rating: 4.8, img: 25 },
        ]?.map((seller) => (
          <div key={seller?.name} className="bg-neutral-01 rounded-2xl p-3 flex items-center gap-3">
            <img
              src={`https://i.pravatar.cc/48?img=${seller?.img}`}
              alt={seller?.name}
              className="w-10 h-10 rounded-full object-cover flex-shrink-0"
            />
            <div className="min-w-0">
              <p className="font-bold text-dark text-sm truncate">{seller?.name}</p>
              <p className="text-xs text-dark/60 truncate">{seller?.niche}</p>
              <p className="text-xs font-semibold text-theme-01">{seller?.gmv}</p>
            </div>
            <div className="ml-auto flex-shrink-0">
              <span className="text-xs font-bold text-dark bg-theme-02 px-2 py-0.5 rounded-full">⭐ {seller?.rating}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
